import {
  createContext,
  Dispatch,
  FC,
  isValidElement,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  ReactNode,
  useContext,
  useEffect,
  useId,
  useRef,
  SetStateAction,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import './FieldHelp.css'

export type FieldHelpRequirement =
  | 'required'
  | 'optional'
  | 'conditional'
  | 'calculated'
  | 'displayOnly'

export type FieldHelpDefinition = {
  key: string
  title: string
  description: string
  requirement: FieldHelpRequirement
  jsonPath?: string
  example?: string
  dataType?: string
  format?: string
  unit?: string
  constraints?: string[]
  condition?: string
  effect?: string
  formula?: string
  commonError?: string
  source?: string
}

type FieldHelpResolver = (labelText: string, helpKey?: string) => FieldHelpDefinition | undefined

const FieldHelpContext = createContext<FieldHelpResolver | undefined>(undefined)
const FieldHelpScopeContext = createContext<
  Dispatch<SetStateAction<FieldHelpResolver | undefined>> | undefined
>(undefined)

const FieldHelpProvider: FC<{resolver: FieldHelpResolver; children: ReactNode}> = ({
  resolver,
  children,
}) => {
  const [scopedResolver, setScopedResolver] = useState<FieldHelpResolver>()
  return (
    <FieldHelpScopeContext.Provider value={setScopedResolver}>
      <FieldHelpContext.Provider value={scopedResolver || resolver}>
        {children}
      </FieldHelpContext.Provider>
    </FieldHelpScopeContext.Provider>
  )
}

const FieldHelpResolverScope: FC<{resolver: FieldHelpResolver}> = ({resolver}) => {
  const setScopedResolver = useContext(FieldHelpScopeContext)

  useEffect(() => {
    if (!setScopedResolver) return
    setScopedResolver(() => resolver)
    return () =>
      setScopedResolver((current: FieldHelpResolver | undefined) =>
        current === resolver ? undefined : current
      )
  }, [resolver, setScopedResolver])

  return null
}

const nodeText = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeText).join(' ')
  if (isValidElement(node)) return nodeText((node.props as {children?: ReactNode}).children)
  return ''
}

const requirementText: Record<FieldHelpRequirement, string> = {
  required: 'اجباری',
  optional: 'اختیاری',
  conditional: 'مشروط به نوع، الگو یا وضعیت صورتحساب',
  calculated: 'محاسبه یا تکمیل خودکار توسط سامانه',
  displayOnly: 'فقط نمایشی؛ در JSON مالیاتی فیلد مستقلی ندارد',
}

const FieldHelpTrigger: FC<{definition: FieldHelpDefinition}> = ({definition}) => {
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [copied, setCopied] = useState(false)
  const [position, setPosition] = useState({top: 0, left: 0, width: 380})
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const tooltipId = useId()
  const open = hovered || pinned

  useEffect(() => {
    if (!open || !triggerRef.current) return

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(380, window.innerWidth - 24)
      const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12))
      const estimatedHeight = tooltipRef.current?.offsetHeight || 230
      const belowTop = rect.bottom + 8
      const top =
        belowTop + estimatedHeight <= window.innerHeight - 12
          ? belowTop
          : Math.max(12, rect.top - estimatedHeight - 8)
      setPosition({top, left, width})
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const closeOnOutsidePointer = (event: globalThis.PointerEvent) => {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !tooltipRef.current?.contains(target)) {
        setPinned(false)
        setHovered(false)
      }
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Escape closes the help first and the dialog it sits in only on the next press.
        event.preventDefault()
        setPinned(false)
        setHovered(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  const toggle = (event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setPinned((current) => !current)
    setHovered(false)
  }

  const stopLabelAction = (event: PointerEvent<HTMLSpanElement>) => {
    event.stopPropagation()
  }

  const copyJsonPath = async () => {
    if (!definition.jsonPath || !navigator.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(definition.jsonPath)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can be blocked by browser permissions; keep the path visible for manual copy.
    }
  }

  const tooltip = open ? (
    <div
      ref={tooltipRef}
      id={tooltipId}
      role='tooltip'
      className='field-help-tooltip'
      style={position}
      dir='rtl'
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => !pinned && setHovered(false)}
    >
      <strong className='field-help-tooltip__title'>{definition.title}</strong>
      <p>{definition.description}</p>
      <div className='field-help-tooltip__requirement'>
        <span>وضعیت:</span>
        <b>{requirementText[definition.requirement]}</b>
      </div>
      {(definition.dataType || definition.format || definition.unit) && (
        <dl className='field-help-tooltip__metadata'>
          {definition.dataType && (
            <div>
              <dt>نوع داده</dt>
              <dd>{definition.dataType}</dd>
            </div>
          )}
          {definition.format && (
            <div>
              <dt>قالب مقدار</dt>
              <dd>{definition.format}</dd>
            </div>
          )}
          {definition.unit && (
            <div>
              <dt>واحد</dt>
              <dd>{definition.unit}</dd>
            </div>
          )}
        </dl>
      )}
      {definition.constraints && definition.constraints.length > 0 && (
        <div className='field-help-tooltip__section'>
          <b>محدودیت‌ها</b>
          <ul>
            {definition.constraints.map((constraint) => (
              <li key={constraint}>{constraint}</li>
            ))}
          </ul>
        </div>
      )}
      {definition.condition && (
        <div className='field-help-tooltip__section'>
          <b>شرایط استفاده و ارسال</b>
          <p>{definition.condition}</p>
        </div>
      )}
      {definition.formula && (
        <div className='field-help-tooltip__section'>
          <b>نحوه تکمیل</b>
          <p>{definition.formula}</p>
        </div>
      )}
      {definition.effect && (
        <div className='field-help-tooltip__section'>
          <b>اثر بر فرم</b>
          <p>{definition.effect}</p>
        </div>
      )}
      {definition.commonError && (
        <div className='field-help-tooltip__section field-help-tooltip__section--warning'>
          <b>خطای رایج و راه‌حل</b>
          <p>{definition.commonError}</p>
        </div>
      )}
      {definition.jsonPath ? (
        <div className='field-help-tooltip__path-row'>
          <code dir='ltr'>{definition.jsonPath}</code>
          <button type='button' onClick={copyJsonPath} aria-label='کپی مسیر JSON'>
            <i className={`las ${copied ? 'la-check' : 'la-copy'}`} aria-hidden='true'></i>
            {copied ? 'کپی شد' : 'کپی'}
          </button>
        </div>
      ) : (
        <div className='field-help-tooltip__no-path'>در JSON ارسالی سازمان مسیر مستقلی ندارد.</div>
      )}
      {definition.example && (
        <div className='field-help-tooltip__example'>
          <span>نمونه:</span>
          <code dir='ltr'>{definition.example}</code>
        </div>
      )}
      {definition.source && (
        <div className='field-help-tooltip__source'>مبنای راهنما: {definition.source}</div>
      )}
    </div>
  ) : null

  return (
    <>
      <span
        ref={triggerRef}
        className='field-help-trigger'
        role='button'
        tabIndex={0}
        aria-label={`راهنمای ${definition.title}`}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        onPointerDown={stopLabelAction}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') toggle(event)
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => !pinned && setHovered(false)}
      >
        <i className='las la-question-circle' aria-hidden='true'></i>
      </span>
      {tooltip && typeof document !== 'undefined' ? createPortal(tooltip, document.body) : null}
    </>
  )
}

type FieldHelpLabelProps = {
  label: ReactNode
  helpKey?: string
  isRequired?: boolean
  className?: string
}

type FieldHelpOptionProps = {
  option: ReactNode
  fieldLabel: ReactNode
  helpKey?: string
  isRequired?: boolean
}

const FieldHelpLabel: FC<FieldHelpLabelProps> = ({label, helpKey, isRequired, className = ''}) => {
  const resolver = useContext(FieldHelpContext)
  const definition = resolver?.(nodeText(label), helpKey)
  if (!definition) return <>{label}</>
  const resolvedDefinition = isRequired
    ? {...definition, requirement: 'required' as const}
    : definition

  return (
    <span className={`field-help-label ${className}`.trim()}>
      <span>{label}</span>
      <FieldHelpTrigger definition={resolvedDefinition} />
    </span>
  )
}

const FieldHelpIcon: FC<Omit<FieldHelpLabelProps, 'className'>> = ({
  label,
  helpKey,
  isRequired,
}) => {
  const resolver = useContext(FieldHelpContext)
  const definition = resolver?.(nodeText(label), helpKey)
  if (!definition) return null
  const resolvedDefinition = isRequired
    ? {...definition, requirement: 'required' as const}
    : definition
  return <FieldHelpTrigger definition={resolvedDefinition} />
}

const FieldHelpOptionIcon: FC<FieldHelpOptionProps> = ({
  option,
  fieldLabel,
  helpKey,
  isRequired,
}) => {
  const resolver = useContext(FieldHelpContext)
  const fieldDefinition = resolver?.(nodeText(fieldLabel), helpKey)
  if (!fieldDefinition) return null

  const optionText = nodeText(option).trim()
  const definition: FieldHelpDefinition = {
    ...fieldDefinition,
    key: `${fieldDefinition.key}.option.${optionText}`,
    title: optionText || fieldDefinition.title,
    description: `این گزینه یکی از مقدارهای قابل انتخاب برای «${fieldDefinition.title}» است. ${fieldDefinition.description}`,
    requirement: isRequired ? 'required' : fieldDefinition.requirement,
  }

  return <FieldHelpTrigger definition={definition} />
}

export {
  FieldHelpIcon,
  FieldHelpLabel,
  FieldHelpOptionIcon,
  FieldHelpProvider,
  FieldHelpResolverScope,
  FieldHelpTrigger,
  nodeText,
}
